import { useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Container,
  TextField,
  Select,
  MenuItem,
  Button,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useFormik } from 'formik';
import * as yup from 'yup';

import { useSnackbar } from 'notistack';
import { ProfileInfoHeader } from '../shared/ProfileInfoHeader';
import { FormPageNavBtns } from '../shared/FormPageNavBtns';
import { FormCardWrap } from '../shared/FormCardWrap';
import { useStyles } from './style';
import { useSendCodeForEnrollMutation } from '../../redux/services/SendCodeForEnroll/SendCodeForEnroll';

import { formCircleBtnNavLink } from '../../utils/generalFunc';
import { dropDownConstList } from '../../config/dropDownConstList';

import { profileInfoAction } from '../../redux/profile/Reducer';

const nextBtnDisabledFunc = (formik) => (Object.keys(formik.errors).length !== 0);

export const EmergencyContactForm = () => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const profile = useSelector((state) => state.profile.profilInfo);
  const errorSendCodeForEnrollApi = useRef(false);
  const { enqueueSnackbar } = useSnackbar();
  const [sendCodeForEnrollApi, {
    isError, isSuccess, isLoading, reset
  }] = useSendCodeForEnrollMutation();

  useEffect(() => {
    if (errorSendCodeForEnrollApi.current) {
      enqueueSnackbar('something went wrong!!', { variant: 'error' });
    }
    errorSendCodeForEnrollApi.current = false;
  }, [errorSendCodeForEnrollApi.current]);

  if (isSuccess) {
    history.push('/phone-verification');
    reset();
  }
  if (isError) {
    errorSendCodeForEnrollApi.current = true;
    reset();
  }

  const postSendCodeForEnrollApi = async () => {
    let phOnlyDigits = profile.Phone.replace(/\D/g, '');
    if (phOnlyDigits.length >= 11) {
      phOnlyDigits = phOnlyDigits.substring(1);
    }
    await sendCodeForEnrollApi({
      donorId: profile.Id,
      phoneNumer: phOnlyDigits
    });
  };

  const formik = useFormik({
    initialValues: {
      FnLName: profile.EmergencyContactName || '',
      email: profile.EmergencyContactEmail || '',
      address: profile.EmergencyContactAddress || '',
      phNo: profile.EmergencyContactPhone || '',
      socialWorkerName: profile.SocialWorkerName || '',
      socialWorkerPh: profile.SocialWorkerPhone || '',
      branchServed: profile.VABranchServed && dropDownConstList.branchServedList.includes(profile.VABranchServed) ? profile.VABranchServed : '',
      dischargeStatus: profile.DischargeStatus && dropDownConstList.dischargeStatusList.includes(profile.DischargeStatus) ? profile.DischargeStatus : '',
    },
    validationSchema: yup.object({
      FnLName: yup
        .string()
        .trim(),
      email: yup
        .string()
        .matches(
          /* eslint-disable no-useless-escape */
          /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
          'Enter a valid email'
        ),
      address: yup
        .string()
        .trim(),
      phNo: yup
        .string()
        .matches(
          /* eslint-disable no-useless-escape */
          /^(?:(?:\+?1\s*(?:[.-]\s*)?)?(?:\(\s*([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9])\s*\)|([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9]))\s*(?:[.-]\s*)?)([2-9]1[02-9]|[2-9][02-9]1|[2-9][02-9]{2})\s*(?:[.-]\s*)?([0-9]{4})(?:\s*(?:#|x\.?|ext\.?|extension)\s*(\d+))?$/,
          'Enter a valid phone number'
        ),
      socialWorkerName: yup
        .string()
        .trim(),
      socialWorkerPh: yup
        .string()
        .matches(
          /* eslint-disable no-useless-escape */
          /^(?:(?:\+?1\s*(?:[.-]\s*)?)?(?:\(\s*([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9])\s*\)|([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9]))\s*(?:[.-]\s*)?)([2-9]1[02-9]|[2-9][02-9]1|[2-9][02-9]{2})\s*(?:[.-]\s*)?([0-9]{4})(?:\s*(?:#|x\.?|ext\.?|extension)\s*(\d+))?$/,
          'Enter a valid phone number'
        ),
      branchServed: yup
        .string()
        .trim(),
      dischargeStatus: yup
        .string()
        .trim(),
    }),
    onSubmit: (data) => {
      // go into next page if statement if button is enabled
      if (!nextBtnDisabledFunc(formik)) {
        dispatch(profileInfoAction({
          ...profile,
          EmergencyContactName: data.FnLName,
          EmergencyContactEmail: data.email,
          EmergencyContactAddress: data.address,
          EmergencyContactPhone: data.phNo,
          SocialWorkerName: data.socialWorkerName,
          SocialWorkerPhone: data.socialWorkerPh,
          VABranchServed: data.branchServed,
          DischargeStatus: data.dischargeStatus,
        }));
        if (profile.Phone) {
          postSendCodeForEnrollApi();
        } else if (profile.InsuranceCardPhotoFront && profile.InsuranceCardPhotoBack) {
          history.push('/card-verify');
        } else {
          history.push('/card-view');
        }
      }
    },
  });

  const onDotClick = (dotNum) => {
    formCircleBtnNavLink(history, dotNum);
  };

  return (
    <Container maxWidth="md" className={classes.emergencyInfoContainer}>
      <ProfileInfoHeader />
      <FormCardWrap>
        <form onSubmit={formik.handleSubmit} className={`formContainer ${classes.emergencyInfoForm}`}>
          <Box className={classes.emergencyContentWrap}>
            <Box>
              <Box className={classes.formPageNumber}>4/4</Box>
              <Box className={classes.contentTitleWrap}>
                <Box component="span" className={`${classes.contentTitleLine} ${classes.titleLine1}`} />
                <Box className={classes.contentTitle}>Emergency Contact</Box>
                <Box component="span" className={`${classes.contentTitleLine} ${classes.titleLine2}`} />
              </Box>
              <Box className={`${classes.nameEmailContainerStyle} ${classes.subTextColor}`}>
                <Box className={classes.nameContainer}>
                  <Box className="commonLabel" component="label">&nbsp;First &amp; Last Name</Box>
                  <TextField
                    name="FnLName"
                    onChange={formik.handleChange}
                    onBlur={() => formik.setFieldTouched('FnLName')}
                    value={formik.values.FnLName || ''}
                    className="inputFormTopMargin"
                    id="FnLName"
                    placeholder="First &amp; Last Name"
                    variant="outlined"
                  />
                </Box>
                <Box className={classes.fullWidth}>
                  <Box className="commonLabel" component="label">&nbsp;Email</Box>
                  <TextField
                    name="email"
                    onChange={formik.handleChange}
                    onBlur={() => formik.setFieldTouched('email')}
                    value={formik.values.email || ''}
                    className="inputFormTopMargin"
                    id="email"
                    placeholder="Email"
                    variant="outlined"
                  />
                </Box>
              </Box>
              <Box className={`${classes.nameEmailContainerStyle} ${classes.subTextColor}`}>
                <Box className={classes.emergencyAddrContainerStyle}>
                  <Box className={`${classes.carrierLabel} commonLabel`} component="label">&nbsp;Address</Box>
                  <TextField
                    name="address"
                    onChange={formik.handleChange}
                    onBlur={() => formik.setFieldTouched('address')}
                    value={formik.values.address || ''}
                    className="inputFormTopMargin"
                    id="address"
                    placeholder="Address"
                    variant="outlined"
                  />
                </Box>
                <Box>
                  <Box className="commonLabel" component="label">&nbsp;Phone</Box>
                  <TextField
                    name="phNo"
                    onChange={formik.handleChange}
                    onBlur={() => formik.setFieldTouched('phNo')}
                    value={formik.values.phNo || ''}
                    className="inputFormTopMargin"
                    id="phNo"
                    placeholder="Phone"
                    variant="outlined"
                  />
                  {formik.touched.phNo && formik.errors.phNo && (
                    <div className={classes.errorText}>{formik.errors.phNo}</div>
                  )}
                </Box>
              </Box>
              <Box className={classes.contentTitleWrap}>
                <Box component="span" className={`${classes.contentTitleLine} ${classes.titleLine1}`} />
                <Box className={`${classes.contentTitle} ${classes.veteranContentTitle}`}>Veterans Affairs/Social Worker Info</Box>
                <Box component="span" className={`${classes.contentTitleLine} ${classes.titleLine2}`} />
              </Box>
              <Box className={`${classes.nameEmailContainerStyle} ${classes.subTextColor}`}>
                <Box className={classes.emergencyAddrContainerStyle}>
                  <Box className="commonLabel" component="label">&nbsp;Social Worker Name</Box>
                  <TextField
                    name="socialWorkerName"
                    onChange={formik.handleChange}
                    onBlur={() => formik.setFieldTouched('socialWorkerName')}
                    value={formik.values.socialWorkerName || ''}
                    className="inputFormTopMargin"
                    id="socialWorkerName"
                    placeholder="Social Worker Name"
                    variant="outlined"
                  />
                </Box>
                <Box>
                  <Box className="commonLabel" component="label">&nbsp;Phone</Box>
                  <TextField
                    name="socialWorkerPh"
                    onChange={formik.handleChange}
                    onBlur={() => formik.setFieldTouched('socialWorkerPh')}
                    value={formik.values.socialWorkerPh || ''}
                    className="inputFormTopMargin"
                    id="socialWorkerPh"
                    placeholder="Phone"
                    variant="outlined"
                  />
                  {formik.touched.socialWorkerPh && formik.errors.socialWorkerPh && (
                    <div className={classes.errorText}>{formik.errors.socialWorkerPh}</div>
                  )}
                </Box>
              </Box>
              <Box className={`${classes.nameEmailContainerStyle} ${classes.subTextColor}`}>
                <Box className={classes.branchServedContainer}>
                  <Box className="commonLabel" component="label">&nbsp;Branch Served In</Box>
                  <Select
                    className="inputFormTopMargin"
                    name="branchServed"
                    onChange={formik.handleChange}
                    onBlur={() => formik.setFieldTouched('branchServed')}
                    value={formik.values.branchServed || ''}
                    displayEmpty
                    inputProps={{ 'aria-label': 'Without label' }}
                    placeholder="Branch Served In"
                  >
                    <MenuItem value="">
                      None
                    </MenuItem>
                    {dropDownConstList.branchServedList.map((value) => <MenuItem value={value} key={value}>{value}</MenuItem>)}

                  </Select>
                </Box>
                <Box className={classes.fullWidth}>
                  <Box className="commonLabel" component="label">&nbsp;Discharge Status</Box>
                  <Select
                    className="inputFormTopMargin"
                    name="dischargeStatus"
                    onChange={formik.handleChange}
                    onBlur={() => formik.setFieldTouched('dischargeStatus')}
                    value={formik.values.dischargeStatus || ''}
                    displayEmpty
                    inputProps={{ 'aria-label': 'Without label' }}
                    placeholder="Discharge Status"
                  >
                    <MenuItem value="">
                      None
                    </MenuItem>
                    {dropDownConstList.dischargeStatusList.map((value) => <MenuItem value={value} key={value}>{value}</MenuItem>)}
                  </Select>
                </Box>
              </Box>
            </Box>
            <Box className={classes.buttonsNavContainer}>
              <Button onClick={() => history.push('/primary-care-info')} variant="contained" color="secondary">
                Back
              </Button>
              <FormPageNavBtns formPageNumber={4} activePgNum={4} onDotClick={onDotClick} />
              <LoadingButton
                loading={isLoading}
                className={`${classes.submitBtn} ${nextBtnDisabledFunc(formik) ? classes.btnDisabled : ''}`}
                type="submit"
                variant="contained"
                color="primary"
              >
                Submit
              </LoadingButton>
            </Box>
          </Box>
        </form>
      </FormCardWrap>
    </Container>
  );
};
